import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useChatContext } from '../ChatContext';
import { SAMPLE_QUESTIONS_BUTTON_WIDTH_PX, SAMPLE_QUESTIONS_GAP_BETWEEN_BUTTONS_PX } from './styles';

type UseSampleQuestionsScrollingResult = {
  containerRef: React.RefObject<HTMLDivElement>;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  hasOverflow: boolean;
  scroll: (direction: 'left' | 'right') => void;
  checkScroll: () => void;
};

const SCROLL_BUFFER = 5;

/**
 * A custom hook that manages horizontal scrolling behavior for product assistant sample questions.
 *
 * This hook handles:
 * - Tracking scroll position and container overflow state
 * - Enabling/disabling scroll buttons based on current position
 * - Implementing smooth scrolling with button-centered snapping
 * - Supporting mouse wheel horizontal scrolling
 * - Recalculating scroll state on window resize and component open/close
 */
export const useSampleQuestionsScrolling = ({
  questionsCount,
  isOpen
}: {
  questionsCount: number;
  isOpen: boolean;
}): UseSampleQuestionsScrollingResult => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isExpanded } = useChatContext();

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const checkScroll = useCallback(() => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      // Greater than 60 so that animation is smoother when going left to first question
      setCanScrollLeft(scrollLeft > 60);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - SCROLL_BUFFER);
      setHasOverflow(scrollWidth > clientWidth);
    }
  }, []);

  const scroll = useCallback(
    (direction: 'left' | 'right') => {
      if (containerRef.current) {
        const scrollAmount = SAMPLE_QUESTIONS_BUTTON_WIDTH_PX + SAMPLE_QUESTIONS_GAP_BETWEEN_BUTTONS_PX;

        // Calculate the current centered button position
        const containerWidth = containerRef.current.clientWidth;
        const currentScrollLeft = containerRef.current.scrollLeft;
        const currentCenterPosition = currentScrollLeft + containerWidth / 2;

        // Find the closest button index to the current center position
        const currentButtonIndex = Math.floor(currentCenterPosition / scrollAmount);

        const targetButtonIndex =
          direction === 'left'
            ? Math.max(0, currentButtonIndex - 1)
            : Math.min(questionsCount - 1, currentButtonIndex + 1);

        // Where to snap the scroll
        const targetScrollLeft =
          targetButtonIndex * scrollAmount - containerWidth / 2 + SAMPLE_QUESTIONS_BUTTON_WIDTH_PX / 2;

        containerRef.current.scrollTo({
          left: Math.max(0, targetScrollLeft),
          behavior: 'smooth'
        });
      }
    },
    [questionsCount]
  );

  useEffect(function handleSampleQuestionsMouseWheel() {
    const container = containerRef.current;
    if (container) {
      const handleWheel = (e: WheelEvent) => {
        e.preventDefault();
        if (e.deltaY !== 0) {
          // Apply a multiplier to increase sensitivity
          container.scrollLeft += e.deltaY * 2.5;
        }
      };

      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }
  }, []);

  useEffect(function handleBodyMouseWheel() {
    const handleWheel = (e: WheelEvent) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      const chatLayoutEl = target.closest('#chat-layout') as HTMLElement | null;
      const chatBodyEl = target.closest('#chat-body') as HTMLElement | null;

      if (chatLayoutEl && (!chatBodyEl || chatBodyEl.scrollHeight <= chatBodyEl.clientHeight)) {
        e.preventDefault();
      }
    };

    document.body.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      document.body.removeEventListener('wheel', handleWheel);
    };
  }, []);

  useEffect(
    function checkScrollOnExpand() {
      setTimeout(() => {
        checkScroll();
      }, 200);
    },
    [checkScroll, isExpanded]
  );

  useEffect(
    function scrollOnOpenAndResize() {
      checkScroll();
      window.addEventListener('resize', checkScroll);
      return () => {
        window.removeEventListener('resize', checkScroll);
      };
    },
    [checkScroll, isOpen]
  );

  return {
    containerRef,
    canScrollLeft,
    canScrollRight,
    hasOverflow,
    scroll,
    checkScroll
  };
};
